import { UnifiedRolesEditorPolicy } from '../abstract/UnifiedRolesEditorPolicy';
import { TenantSpecificTenantAdminRolesEnum } from '../enums';
import {
  IUnifiedRolesEditorPolicy,
  TUnifiedRoleCollectionDto,
  TUnifiedRolesMap,
} from '../interfaces';
import { UnifiedRolesEditor } from '../UnifiedRolesEditor';

export class UnifiedRolesEditorResolver implements UnifiedRolesEditorPolicy {
  public checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole: UnifiedRolesEditorPolicy['checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole'];
  public checkUnifiedRoleIsTenantAdminUnifiedRole: UnifiedRolesEditorPolicy['checkUnifiedRoleIsTenantAdminUnifiedRole'];
  public checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole: UnifiedRolesEditorPolicy['checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole'];

  constructor(
    private readonly payload: {
      unifiedRolesAssignedToResource: TUnifiedRoleCollectionDto;
      allAvailableUnifiedRoles: TUnifiedRoleCollectionDto;
      unifiedRolesEditor: UnifiedRolesEditor;
    },
  ) {
    this.checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole =
      this.payload.unifiedRolesEditor.checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole;

    this.checkUnifiedRoleIsTenantAdminUnifiedRole =
      this.payload.unifiedRolesEditor.checkUnifiedRoleIsTenantAdminUnifiedRole;

    this.checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole =
      this.payload.unifiedRolesEditor.checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole;
  }

  public static create(payload: {
    unifiedRolesAssignedToResource: TUnifiedRoleCollectionDto;
    allAvailableUnifiedRoles: TUnifiedRoleCollectionDto;
    unifiedRolesEditor: UnifiedRolesEditor;
  }): UnifiedRolesEditorPolicy {
    return new UnifiedRolesEditorResolver(payload);
  }

  public readonly composeResourceTenantAgnosticRolesMap: IUnifiedRolesEditorPolicy['composeResourceTenantAgnosticRolesMap'] =
    () => {
      return this.payload.unifiedRolesEditor.composeResourceTenantAgnosticRolesMap(
        {
          availableTenantAgnosticRolesCollection:
            this.payload.allAvailableUnifiedRoles,
          assignedTenantAgnosticRolesCollection:
            this.payload.unifiedRolesAssignedToResource,
        },
      );
    };

  public readonly composeResourceTenantSpecificRolesMap: IUnifiedRolesEditorPolicy['composeResourceTenantSpecificRolesMap'] =
    () => {
      return this.payload.unifiedRolesEditor.composeResourceTenantSpecificRolesMap(
        {
          availableTenantSpecificRoles: this.payload.allAvailableUnifiedRoles,
          assignedTenantSpecificRoles:
            this.payload.unifiedRolesAssignedToResource,
        },
      );
    };

  public readonly composeUserTenantSpecificTenantAdminRoles: IUnifiedRolesEditorPolicy['composeUserTenantSpecificTenantAdminRoles'] =
    (
      tenantSpecificTenantAdminRolesType: TenantSpecificTenantAdminRolesEnum,
    ) => {
      const {
        tenantAdminRole,
        nmcLegacyTenantAdminRole,
        nmcLegacyReadOnlyRole,
      } =
        this.payload.unifiedRolesEditor.getAllTenantSpecificAdministrationRoles(
          {
            tenantSpecificRoles: this.payload.allAvailableUnifiedRoles,
          },
        );

      const userTenantSpecificRoles =
        this.composeResourceTenantSpecificRolesMap();

      const tenantAdminRoles = [tenantAdminRole, nmcLegacyTenantAdminRole];
      const nonTenantAdminRoles = [nmcLegacyReadOnlyRole];

      switch (tenantSpecificTenantAdminRolesType) {
        case TenantSpecificTenantAdminRolesEnum.WITH_TENANT_SPECIFIC_TENANT_ADMIN_ROLES:
          return this.batchUnifiedRoles({
            roles: userTenantSpecificRoles,
            rolesToAdd: tenantAdminRoles,
            rolesToRemove: nonTenantAdminRoles,
          });
        case TenantSpecificTenantAdminRolesEnum.WITH_NON_TENANT_ADMIN_ROLES:
          return this.batchUnifiedRoles({
            roles: userTenantSpecificRoles,
            rolesToAdd: nonTenantAdminRoles,
            rolesToRemove: tenantAdminRoles,
          });
        default:
          throw new Error('unknown tenantSpecificTenantAdminRolesType');
      }
    };

  public readonly batchUnifiedRoles: IUnifiedRolesEditorPolicy['batchUnifiedRoles'] =
    ({ roles, rolesToAdd, rolesToRemove }) => {
      const payload = {
        roles: new Map(roles),
      };

      for (const role of rolesToAdd) {
        payload.roles = this.addUnifiedRole(payload.roles, role.id);
      }

      for (const role of rolesToRemove) {
        payload.roles = this.removeUnifiedRole(payload.roles, role.id);
      }

      return payload.roles;
    };

  public readonly removeUnifiedRole: IUnifiedRolesEditorPolicy['removeUnifiedRole'] =
    (targetUnifiedRolesMap, unifiedRoleIdToRemove) => {
      return this.payload.unifiedRolesEditor.removeRoleFromRolesMap({
        targetUnifiedRolesMap,
        unifiedRoleIdToRemove,
      });
    };

  public readonly removeAllUnifiedRoles: IUnifiedRolesEditorPolicy['removeAllUnifiedRoles'] =
    (targetUnifiedRolesMap) => {
      return this.payload.unifiedRolesEditor.removeAllRolesFromRolesMap({
        targetUnifiedRolesMap,
      });
    };

  public readonly addUnifiedRole: IUnifiedRolesEditorPolicy['addUnifiedRole'] =
    (targetUnifiedRolesMap, unifiedRoleIdToAdd) => {
      return this.payload.unifiedRolesEditor.addRoleToRolesMap({
        targetUnifiedRolesMap,
        unifiedRoleIdToAdd,
        sourceUnifiedRolesCollection: this.payload.allAvailableUnifiedRoles,
      });
    };

  public readonly addAllUnifiedRoles: IUnifiedRolesEditorPolicy['addAllUnifiedRoles'] =
    (targetUnifiedRolesMap) => {
      return this.payload.unifiedRolesEditor.addAllRolesToRolesMap({
        targetUnifiedRolesMap,
        assignedRolesCollection: this.payload.unifiedRolesAssignedToResource,
        availableRolesCollection: this.payload.allAvailableUnifiedRoles,
      });
    };

  public readonly toUnifiedRoleCollectionDto: IUnifiedRolesEditorPolicy['toUnifiedRoleCollectionDto'] =
    (unifiedRolesMap) => {
      return this.payload.unifiedRolesEditor.composeRoleCollectionDtoFromRolesMap(
        {
          unifiedRolesMap,
        },
      );
    };

  public readonly canUserPrincipalUpdateTenantAgnosticUnifiedRoles: IUnifiedRolesEditorPolicy['canUserPrincipalUpdateTenantAgnosticUnifiedRoles'] =
    (userPrincipal) => {
      return userPrincipal.hasAnyOfPermissions([
        // @ts-expect-error string[] !== Authority[]
        'system-admin:update:tenant-agnostic-user-role-assignments',
      ]);
    };

  public readonly canUserPrincipalUpdateTenantSpecificUnifiedRoles: IUnifiedRolesEditorPolicy['canUserPrincipalUpdateTenantSpecificUnifiedRoles'] =
    (userPrincipal) => {
      return userPrincipal.hasAnyOfPermissions([
        // @ts-expect-error string[] !== Authority[]
        'system-admin:update:tenant-specific-user-role-assignments',
        // @ts-expect-error string[] !== Authority[]
        'SAVE_USER_ROLES',
      ]);
    };

  public readonly getAllAvailableUnifiedRoles: IUnifiedRolesEditorPolicy['getAllAvailableUnifiedRoles'] =
    () => {
      return this.payload.allAvailableUnifiedRoles;
    };

  public readonly getPrevAssignedResourceUnifiedRoles: IUnifiedRolesEditorPolicy['getPrevAssignedResourceUnifiedRoles'] =
    () => {
      return this.payload.unifiedRolesAssignedToResource;
    };

  public readonly hasUnifiedRoleAssigned: IUnifiedRolesEditorPolicy['hasUnifiedRoleAssigned'] =
    (unifiedRoleToCheck) => {
      return !!this.payload.unifiedRolesAssignedToResource
        .toSerializedModel()
        .roles.find(({ id }) => id === unifiedRoleToCheck.id);
    };

  public readonly isRoleTenantAdminRelatedRole: IUnifiedRolesEditorPolicy['isRoleTenantAdminRelatedRole'] =
    (maybeTenantAdminRelatedRole) => {
      return this.payload.unifiedRolesEditor.isRoleTenantAdminRelatedRole(
        maybeTenantAdminRelatedRole,
      );
    };

  public readonly compareUnifiedRolesState: IUnifiedRolesEditorPolicy['compareUnifiedRolesState'] =
    (payload) => {
      return this.payload.unifiedRolesEditor.compareResourceUnifiedRolesState(
        payload,
      );
    };

  public readonly addAllNonTenantAdminRelatedRolesToAssignedRoles: IUnifiedRolesEditorPolicy['addAllNonTenantAdminRelatedRolesToAssignedRoles'] =
    (assignedRoles) => {
      return this.payload.unifiedRolesEditor.addAllNonTenantAdminRelatedRolesToAssignedRoles(
        {
          targetUnifiedRolesMap: assignedRoles,
          assignedRolesCollection: this.payload.unifiedRolesAssignedToResource,
          availableRolesCollection: this.payload.allAvailableUnifiedRoles,
        },
      );
    };

  public removeAllNonTenantAdminRelatedRolesFromAssignedRoles(
    assignedRoles: TUnifiedRolesMap,
  ) {
    return this.payload.unifiedRolesEditor.removeAllNonTenantAdminRelatedRolesFromAssignedRoles(
      {
        targetUnifiedRolesMap: assignedRoles,
      },
    );
  }
}
